import { TalentChekRole } from '@/types/auth.types';

/**
 * All TalentChek-specific roles.
 */
export const TALENTCHEK_ROLES = [
  TalentChekRole.STAFF,
  TalentChekRole.GLOBAL_HR,
  TalentChekRole.COUNTRY_HR,
  TalentChekRole.ADMIN,
  TalentChekRole.COUNTRY_MANAGER,
] as const;

/**
 * Check if a roles array includes a specific TalentChek role.
 */
export function hasTalentChekRole(roles: string[], role: TalentChekRole): boolean {
  return roles.some(r => r.toUpperCase() === role.toUpperCase());
}

/**
 * Check if the user has any TalentChek role.
 */
export function hasAnyTalentChekRole(roles: string[]): boolean {
  return TALENTCHEK_ROLES.some(tcRole => hasTalentChekRole(roles, tcRole));
}

/**
 * Is the user a Global HR?
 * Note: Admins inherently have all Global HR rights.
 */
export function isGlobalHR(roles: string[]): boolean {
  return hasTalentChekRole(roles, TalentChekRole.GLOBAL_HR) || hasTalentChekRole(roles, TalentChekRole.ADMIN);
}

/**
 * Is the user an Admin?
 */
export function isAdmin(roles: string[]): boolean {
  return hasTalentChekRole(roles, TalentChekRole.ADMIN);
}

/**
 * Is the user a Country HR?
 */
export function isCountryHR(roles: string[]): boolean {
  return hasTalentChekRole(roles, TalentChekRole.COUNTRY_HR);
}

/**
 * Is the user a Country Manager?
 */
export function isCountryManager(roles: string[]): boolean {
  return hasTalentChekRole(roles, TalentChekRole.COUNTRY_MANAGER);
}

/**
 * Is the user ONLY a staff member (has STAFF but NOT GLOBAL_HR or COUNTRY_HR)?
 */
export function isStaffOnly(roles: string[]): boolean {
  return (
    hasTalentChekRole(roles, TalentChekRole.STAFF) &&
    !isGlobalHR(roles) &&
    !isCountryHR(roles)
  );
}

/**
 * Can the user see the Employee navigation section?
 * Only GLOBAL_HR and COUNTRY_HR can. STAFF-only cannot.
 */
export function canViewEmployeeNav(roles: string[]): boolean {
  return isGlobalHR(roles) || isCountryHR(roles);
}

/**
 * Returns the country code to filter data by, or null if no filter applies.
 * - COUNTRY_HR or COUNTRY_MANAGER: returns their country code (data is restricted)
 * - GLOBAL_HR: returns null (sees everything)
 * - STAFF: returns null (no access anyway)
 */
export function getUserCountryFilter(roles: string[], userCountry: string | undefined): string | null {
  if ((isCountryHR(roles) || isCountryManager(roles)) && !isGlobalHR(roles) && userCountry) {
    return userCountry;
  }
  return null;
}

/**
 * Returns a human-readable primary TalentChek role for display purposes.
 */
export function getPrimaryTalentChekRole(roles: string[]): string | undefined {
  if (isAdmin(roles)) return 'Admin';
  if (isGlobalHR(roles)) return 'Global HR';
  if (isCountryHR(roles)) return 'Country HR';
  if (isCountryManager(roles)) return 'Country Manager';
  if (hasTalentChekRole(roles, TalentChekRole.STAFF)) return 'Staff';
  return undefined;
}
